import React from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { MdDownload, MdZoomIn } from 'react-icons/md';
import { PLATFORMS, PLATFORM_ORDER } from '../data/platforms/index';

const PLATFORM_TAB_ORDER = ['all', ...PLATFORM_ORDER];

const IconCard = observer(({ icon, store }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const checkerboard = `
    repeating-conic-gradient(#e0e0e0 0% 25%, #f8f8f8 0% 50%) 0 0 / 10px 10px
  `;

  return (
    <Box
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      bg={cardBg}
      overflow="hidden"
      shadow="xs"
      role="group"
    >
      <Box
        position="relative"
        pt="100%"
        bg={checkerboard}
        cursor="pointer"
        onClick={() => store.setZoomedIcon(icon)}
      >
        <img
          src={icon.dataUrl}
          alt={icon.filename}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '80%',
            maxHeight: '80%',
            objectFit: 'contain',
          }}
        />
        <Box
          position="absolute"
          inset={0}
          bg="blackAlpha.500"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.15s"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={MdZoomIn} color="white" boxSize={6} />
        </Box>
      </Box>
      <Flex align="center" justify="space-between" px={2} py={1.5} gap={1}>
        <Box minW={0} flex={1}>
          <Text fontSize="9px" fontWeight="semibold" color={mutedColor} isTruncated title={icon.filename}>
            {icon.filename}
          </Text>
          <Text fontSize="9px" color="gray.400">{icon.size}×{icon.size}</Text>
        </Box>
        <Tooltip label="Download" placement="top" hasArrow openDelay={400}>
          <IconButton
            aria-label="Download icon"
            icon={<MdDownload />}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            onClick={() => store.downloadSingleIcon(icon)}
            flexShrink={0}
          />
        </Tooltip>
      </Flex>
    </Box>
  );
});

const PreviewGrid = observer(({ store }) => {
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeBorder = useColorModeValue('blue.300', 'blue.500');
  const tabBg = useColorModeValue('gray.100', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const modalBg = useColorModeValue('white', 'gray.900');

  const icons = store.filteredIcons;

  return (
    <Box>
      {/* Platform filter tabs */}
      <Flex gap={1.5} mb={4} flexWrap="wrap">
        {PLATFORM_TAB_ORDER.map((tabId) => {
          const isAll = tabId === 'all';
          const platform = isAll ? null : PLATFORMS[tabId];
          const count = isAll
            ? store.generatedIcons.length
            : store.generatedIcons.filter((i) => i.platformId === tabId).length;
          if (!isAll && count === 0) return null;
          const isActive = store.previewPlatform === tabId;

          return (
            <Button
              key={tabId}
              size="xs"
              borderRadius="full"
              borderWidth="1px"
              borderColor={isActive ? activeBorder : 'transparent'}
              bg={isActive ? activeBg : tabBg}
              fontWeight={isActive ? 'semibold' : 'normal'}
              onClick={() => store.setPreviewPlatform(tabId)}
              _hover={{ borderColor: activeBorder }}
            >
              {isAll ? '✦ All' : `${platform.emoji} ${platform.label}`}
              <Text as="span" ml={1} fontSize="9px" color={mutedColor}>
                {count}
              </Text>
            </Button>
          );
        })}
      </Flex>

      {/* Grid */}
      {icons.length === 0 ? (
        <Flex justify="center" align="center" h={32}>
          <Text fontSize="sm" color={mutedColor}>No icons for this platform</Text>
        </Flex>
      ) : (
        <SimpleGrid
          columns={{ base: 4, sm: 5, md: 6, lg: 8 }}
          spacing={2}
        >
          {icons.map((icon, idx) => (
            <IconCard key={`${icon.platformId}-${icon.filename}-${idx}`} icon={icon} store={store} />
          ))}
        </SimpleGrid>
      )}

      {/* Zoom modal */}
      <Modal
        isOpen={!!store.zoomedIcon}
        onClose={() => store.clearZoom()}
        isCentered
        size="sm"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={modalBg} borderRadius="2xl">
          <ModalHeader fontSize="sm" pb={1}>
            {store.zoomedIcon?.filename}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {store.zoomedIcon && (
              <Box>
                <Grid
                  placeItems="center"
                  bg="repeating-conic-gradient(#e0e0e0 0% 25%, #f8f8f8 0% 50%) 0 0 / 16px 16px"
                  borderRadius="lg"
                  p={6}
                  mb={3}
                >
                  <img
                    src={store.zoomedIcon.dataUrl}
                    alt={store.zoomedIcon.filename}
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
                  />
                </Grid>
                <Text fontSize="xs" color={mutedColor} textAlign="center">
                  {store.zoomedIcon.size} × {store.zoomedIcon.size} px &nbsp;·&nbsp; {store.zoomedIcon.label}
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<MdDownload />}
              onClick={() => {
                store.downloadSingleIcon(store.zoomedIcon);
                store.clearZoom();
              }}
            >
              Download
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default PreviewGrid;
